import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';

const AchievementsContainer = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  border: '1px solid rgba(102, 126, 234, 0.2)',
  textAlign: 'center',
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)',
  },
}));

const AchievementCard = styled(Paper)(({ theme, unlocked }) => ({
  background: unlocked ? '#1a1a2e' : 'rgba(26, 26, 46, 0.7)',
  borderRadius: '16px',
  padding: theme.spacing(2),
  border: `2px solid ${unlocked ? '#667eea' : 'rgba(102, 126, 234, 0.1)'}`,
  textAlign: 'center',
  position: 'relative',
  opacity: unlocked ? 1 : 0.6,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: unlocked ? 'translateY(-4px)' : 'none',
    boxShadow: unlocked ? '0 8px 24px rgba(102, 126, 234, 0.2)' : 'none',
  },
}));

const StreakBadge = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  color: '#ffffff',
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)',
}));

const achievements = [
  {
    id: 'first_module',
    name: 'Primeiro Passo',
    description: 'Complete seu primeiro módulo',
    icon: SchoolIcon,
    requirement: { type: 'modules_completed', value: 1 },
    points: 100,
  },
  {
    id: 'quiz_master',
    name: 'Mestre dos Quizzes',
    description: 'Acerte 90% em 5 quizzes diferentes',
    icon: EmojiEventsIcon,
    requirement: { type: 'quiz_high_scores', value: 5 },
    points: 250,
  },
  {
    id: 'study_streak_7',
    name: 'Semana de Dedicação',
    description: 'Estude por 7 dias consecutivos',
    icon: LocalFireDepartmentIcon,
    requirement: { type: 'study_streak', value: 7 },
    points: 150,
  },
  {
    id: 'flashcard_pro',
    name: 'Mestre dos Flashcards',
    description: 'Domine 100 flashcards',
    icon: StarIcon,
    requirement: { type: 'flashcards_mastered', value: 100 },
    points: 200,
  },
  {
    id: 'speed_reader',
    name: 'Leitor Veloz',
    description: 'Leia 10 módulos completos',
    icon: TimerIcon,
    requirement: { type: 'modules_read', value: 10 },
    points: 300,
  },
  {
    id: 'perfect_score',
    name: 'Perfeição',
    description: 'Obtenha 100% em um quiz',
    icon: CheckCircleIcon,
    requirement: { type: 'perfect_quiz', value: 1 },
    points: 100,
  },
];

export default function Achievements({ userId }) {
  const [userStats, setUserStats] = useState({
    totalPoints: 0,
    currentStreak: 0,
    bestStreak: 0,
    modulesCompleted: 0,
    quizzesCompleted: 0,
    perfectQuizzes: 0,
    flashcardsMastered: 0,
    studyTime: 0,
    level: 1,
  });
  const [unlockedAchievements, setUnlockedAchievements] = useState(new Set());
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  useEffect(() => {
    // Load user stats and achievements from localStorage
    const savedStats = localStorage.getItem(`user-stats-${userId}`);
    const savedAchievements = localStorage.getItem(`user-achievements-${userId}`);
    
    if (savedStats) {
      setUserStats(JSON.parse(savedStats));
    }
    
    if (savedAchievements) {
      setUnlockedAchievements(new Set(JSON.parse(savedAchievements)));
    }
  }, [userId]);

  const checkAchievements = () => {
    const newUnlocked = new Set(unlockedAchievements);
    let pointsEarned = 0;

    achievements.forEach(achievement => {
      if (!unlockedAchievements.has(achievement.id)) {
        let unlocked = false;
        
        switch (achievement.requirement.type) {
          case 'modules_completed':
            unlocked = userStats.modulesCompleted >= achievement.requirement.value;
            break;
          case 'quiz_high_scores':
            unlocked = userStats.perfectQuizzes >= achievement.requirement.value;
            break;
          case 'study_streak':
            unlocked = userStats.currentStreak >= achievement.requirement.value;
            break;
          case 'flashcards_mastered':
            unlocked = userStats.flashcardsMastered >= achievement.requirement.value;
            break;
          case 'modules_read':
            unlocked = userStats.modulesCompleted >= achievement.requirement.value;
            break;
          case 'perfect_quiz':
            unlocked = userStats.perfectQuizzes >= achievement.requirement.value;
            break;
        }
        
        if (unlocked) {
          newUnlocked.add(achievement.id);
          pointsEarned += achievement.points;
        }
      }
    });

    if (pointsEarned > 0) {
      setUnlockedAchievements(newUnlocked);
      setUserStats(prev => ({ ...prev, totalPoints: prev.totalPoints + pointsEarned }));
      
      // Save to localStorage
      localStorage.setItem(`user-achievements-${userId}`, JSON.stringify([...newUnlocked]));
      localStorage.setItem(`user-stats-${userId}`, JSON.stringify({
        ...userStats,
        totalPoints: userStats.totalPoints + pointsEarned,
      }));
    }
  };

  useEffect(() => {
    checkAchievements();
  }, [userStats]);

  const calculateLevel = (points) => {
    return Math.floor(points / 500) + 1;
  };

  const calculateLevelProgress = (points) => {
    return (points % 500) / 500 * 100;
  };

  const level = calculateLevel(userStats.totalPoints);
  const levelProgress = calculateLevelProgress(userStats.totalPoints);

  return (
    <AchievementsContainer elevation={0}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <EmojiEventsIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
          Conquistas e Progresso
        </Typography>
        <Typography variant="body1" sx={{ color: '#cbd5e1' }}>
          Acompanhe seu desempenho e desbloqueie recompensas
        </Typography>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <StatsCard elevation={0}>
            <TrendingUpIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
              {userStats.totalPoints}
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
              Pontos Totais
            </Typography>
          </StatsCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatsCard elevation={0}>
            <Box sx={{ position: 'relative' }}>
              <StreakBadge>
                <LocalFireDepartmentIcon sx={{ fontSize: 40 }} />
              </StreakBadge>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
                {userStats.currentStreak}
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                Dias Consecutivos
              </Typography>
            </Box>
          </StatsCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatsCard elevation={0}>
            <SchoolIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
              {userStats.modulesCompleted}
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
              Módulos Completos
            </Typography>
          </StatsCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatsCard elevation={0}>
            <StarIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
              Nível {level}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={levelProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(102, 126, 234, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Achievements Grid */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#f1f5f9' }}>
        Suas Conquistas
      </Typography>
      
      <Grid container spacing={2}>
        {achievements.map(achievement => {
          const Icon = achievement.icon;
          const unlocked = unlockedAchievements.has(achievement.id);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={achievement.id}>
              <AchievementCard 
                elevation={0} 
                unlocked={unlocked}
                onClick={() => setSelectedAchievement(achievement)}
              >
                {!unlocked && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <LockIcon sx={{ fontSize: 20, color: '#cbd5e1' }} />
                  </Box>
                )}
                <Icon sx={{ fontSize: 48, color: unlocked ? '#667eea' : '#64748b', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#f1f5f9', mb: 0.5 }}>
                  {achievement.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>
                  {achievement.description}
                </Typography>
                <Chip
                  label={`${achievement.points} pontos`}
                  size="small"
                  color={unlocked ? 'primary' : 'default'}
                  variant={unlocked ? 'filled' : 'outlined'}
                />
              </AchievementCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Achievement Detail Dialog */}
      <Dialog
        open={Boolean(selectedAchievement)}
        onClose={() => setSelectedAchievement(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAchievement && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedAchievement.name}
              </Typography>
              <IconButton onClick={() => setSelectedAchievement(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                {React.createElement(selectedAchievement.icon, { 
                  sx: { fontSize: 80, color: '#667eea', mb: 2 } 
                })}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedAchievement.description}
                </Typography>
                <Chip
                  label={`Recompensa: ${selectedAchievement.points} pontos`}
                  color="primary"
                  size="large"
                />
                {unlockedAchievements.has(selectedAchievement.id) && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                    ✓ Conquista desbloqueada!
                  </Typography>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </AchievementsContainer>
  );
}